import React, { useMemo, useState } from "react";
import { useParams, useSearchParams, Link, Navigate } from "react-router-dom";
import useLive from "../hooks/useLive";
import useHistory from "../hooks/useHistory";
import useSwap from "../hooks/useSwap";
import { useWallet } from "../context/WalletContext";
import { sparkline, deltaOf, isListedTicker } from "../data/mockData";

const fmt = (v, d = 2) => (v == null ? "—" : Number(v).toFixed(d));

function DeltaText({ d }) {
  if (d == null) return <span style={{ color: "var(--ink-muted)" }}>—</span>;
  const cls = d > 0 ? "up" : d < 0 ? "down" : "";
  const sign = d > 0 ? "+" : d < 0 ? "−" : "±";
  return <span className={cls}>{sign}{Math.abs(d).toFixed(2)}%</span>;
}

function TimeChart({ ticker, delta, halted, history }) {
  const W = 780, H = 260;

  // Use real chain history if we have at least 4 points, else deterministic sparkline
  const usingHistory = Array.isArray(history) && history.length >= 4;

  let poolPts, navPts;
  if (usingHistory) {
    poolPts = history.map((h) => (h.pool == null ? null : Number(h.pool)));
    navPts  = history.map((h) => (h.nav  == null ? null : Number(h.nav)));
    // Fill nulls with previous value
    let last = null;
    poolPts = poolPts.map((v) => (last = v ?? last));
    last = null;
    navPts  = navPts.map((v) => (last = v ?? last));
  } else {
    const d = delta == null ? 0 : delta;
    poolPts = sparkline(ticker.charCodeAt(0) + 1, d);
    navPts  = sparkline(ticker.charCodeAt(0) + 9, d * 0.55);
  }

  const flatPool = poolPts.filter((v) => v != null);
  const flatNav  = navPts.filter((v) => v != null);
  const all = [...flatPool, ...flatNav];
  if (all.length === 0) return <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} />;
  const min = Math.min(...all), max = Math.max(...all);
  const rng = max - min || 1;
  const step = W / Math.max(1, poolPts.length - 1);
  const build = (pts) =>
    pts.map((y, i) => {
      if (y == null) return "";
      const px = (i * step).toFixed(2);
      const py = (H - ((y - min) / rng) * (H - 24) - 12).toFixed(2);
      return `${i === 0 ? "M" : "L"}${px},${py}`;
    }).join(" ");
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" data-testid="pool-nav-chart">
      <path d={build(navPts)}  stroke="var(--line)" strokeWidth="1.25" fill="none" strokeDasharray="3 3" />
      <path d={build(poolPts)} stroke={halted ? "var(--ink-muted)" : "var(--ink)"} strokeWidth="1.5" fill="none" />
      {!usingHistory && (
        <text x={W-6} y={14} textAnchor="end" fontSize="9" letterSpacing="1.4"
              fill="var(--ink-muted)" fontFamily="Inter, sans-serif">
          BUILDING HISTORY
        </text>
      )}
    </svg>
  );
}

function RangePanel({ ticker, pool }) {
  const anchor = pool || 100;
  const [tier, setTier] = useState("0.30");
  const [low, setLow]   = useState((anchor * 0.94).toFixed(2));
  const [high, setHigh] = useState((anchor * 1.05).toFixed(2));
  const [amt, setAmt]   = useState("");
  const width = Math.max(1, Number(high) - Number(low));
  const inside = pool != null && pool >= Number(low) && pool <= Number(high);
  const est30d = amt ? (Number(amt) * (Number(tier) / 100) * 8).toFixed(2) : "0.00";

  return (
    <div data-testid="range-panel" style={{ marginTop: 8 }}>
      <div className="grotesque" style={{ fontSize: 10.5, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-muted)", marginBottom: 12 }}>
        Pair {ticker}/USDG · Draft here, fill on Delta
      </div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        {["0.01", "0.05", "0.30", "1.00"].map((t) => (
          <button key={t} onClick={() => setTier(t)} className="btn-ghost" data-testid={`fee-tier-${t}`} style={{ opacity: tier === t ? 1 : 0.4 }}>{t}%</button>
        ))}
      </div>
      <div className="range-inputs-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
        <div>
          <label style={{ fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-muted)" }}>Low</label>
          <input value={low} onChange={(e) => setLow(e.target.value)} data-testid="range-low"
                 style={{ width: "100%", padding: "10px 8px", marginTop: 6, background: "transparent", border: "1px solid var(--line)", color: "var(--ink)", fontSize: 14 }} />
        </div>
        <div>
          <label style={{ fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-muted)" }}>High</label>
          <input value={high} onChange={(e) => setHigh(e.target.value)} data-testid="range-high"
                 style={{ width: "100%", padding: "10px 8px", marginTop: 6, background: "transparent", border: "1px solid var(--line)", color: "var(--ink)", fontSize: 14 }} />
        </div>
        <div>
          <label style={{ fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-muted)" }}>Amount USDG</label>
          <input value={amt} onChange={(e) => setAmt(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0.00" data-testid="range-amount"
                 style={{ width: "100%", padding: "10px 8px", marginTop: 6, background: "transparent", border: "1px solid var(--line)", color: "var(--ink)", fontSize: 14 }} />
        </div>
      </div>
      <div style={{ position: "relative", height: 44, border: "1px solid var(--line)", background: "var(--paper-raised)", marginBottom: 14 }}>
        <div style={{ position: "absolute", top: 0, bottom: 0, left: "10%", right: "10%", borderLeft: "1px solid var(--ink)", borderRight: "1px solid var(--ink)", background: "rgba(18,21,26,0.05)" }}>
          <div style={{ position: "absolute", top: 6, left: 8, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase" }}>{low}</div>
          <div style={{ position: "absolute", top: 6, right: 8, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase" }}>{high}</div>
          {pool != null && (
            <div style={{ position: "absolute", top: 0, bottom: 0, left: `${Math.min(100, Math.max(0, ((pool - Number(low)) / width) * 100))}%`, width: 2, background: "var(--ink)" }} data-testid="range-price-tick" />
          )}
          {pool != null && (
            <div style={{ position: "absolute", bottom: 4, left: `calc(${Math.min(100, Math.max(0, ((pool - Number(low)) / width) * 100))}% + 4px)`, fontSize: 10, color: "var(--ink-muted)", letterSpacing: "0.12em", textTransform: "uppercase" }}>
              pool {pool.toFixed(2)}
            </div>
          )}
        </div>
      </div>
      <div className="tabular" style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--ink-muted)" }}>
        <span>Notional {amt || "0.00"} USDG</span>
        <span>Inside band {inside ? "yes" : "no"}</span>
        <span>Est 30d fees ~ {est30d} USDG</span>
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
        <button className="btn-primary" data-testid="range-add" disabled style={{ opacity: 0.35 }}>Add range · Preview</button>
        <a href="https://delta.example" onClick={(e) => e.preventDefault()} className="btn-outline" data-testid="range-open-delta" style={{ textDecoration: "none" }}>Open in Delta</a>
      </div>
      <div style={{ color: "var(--ink-muted)", fontSize: 11.5, marginTop: 14 }}>
        Set the range here. Fill it on Delta. Fees are estimates. Not financial advice.
      </div>
    </div>
  );
}

export default function ListedTicket() {
  const { ticker } = useParams();
  const { listed, pairs } = useLive();
  const { wallet, setOpen } = useWallet();
  const t = (ticker || "").toUpperCase();
  const row = listed.find((r) => r.ticker === t);
  const [params, setParams] = useSearchParams();
  const [side, setSide] = useState(params.get("side") === "sell" ? "sell" : "buy");
  const [pay, setPay]   = useState("USDG");
  const [size, setSize] = useState(params.get("size") || "");
  const [slippage, setSlippage] = useState("0.5");
  const [tf, setTf]     = useState("1D");
  const [tab, setTab]   = useState(params.get("tab") === "range" ? "range" : "trade");
  const [sheetOpen, setSheetOpen] = useState(false);

  const history = useHistory(t, tf);
  const { submit: submitSwap, busy: swapBusy, stage: swapStage } = useSwap({ wallet });

  if (!isListedTicker(t)) return <Navigate to="/listed" replace />;
  if (!row) return <div style={{ padding: 40, color: "var(--ink-muted)" }}>Reading from chain…</div>;

  const halted = row.session === "HALT";
  const d = deltaOf(row);
  const connected = !!wallet;
  const wrongChain = connected && wallet.chainId !== 4663;
  const canTrade = connected && !wrongChain && !halted && !!row.fee && !!row.pool;

  const setSideHandler = (s) => { setSide(s); const p = new URLSearchParams(params); p.set("side", s); setParams(p); };
  const setTabHandler  = (t2) => { setTab(t2); const p = new URLSearchParams(params); p.set("tab", t2); setParams(p); };

  const priceImpact = size ? Math.min(9.9, Number(size) / 5000) : 0;
  const relatedPairs = pairs.filter(
    (p) => p.pair.startsWith(row.ticker + "/") || p.pair.endsWith("/" + row.ticker)
  );

  return (
    <div className="ticket-page" style={{ maxWidth: 1520, margin: "0 auto", padding: "40px 28px 20px" }}>
      <div className="ticket-head">
        <div className="ticket-head-left" style={{ display: "flex", alignItems: "baseline", gap: 20 }}>
          <div className="wordmark" style={{ fontSize: 68, letterSpacing: "-0.06em", lineHeight: 1 }} data-testid="ticket-ticker">{row.ticker}</div>
          <div className="tabular" style={{ fontSize: 26 }}>{fmt(row.pool)}</div>
          <div style={{ color: "var(--ink-muted)", fontSize: 13 }}>NAV {fmt(row.nav)}</div>
          <div style={{ fontSize: 13 }}><DeltaText d={d} /></div>
        </div>
        <div className="ticket-head-right" style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span className={`chip ${row.session === "HALT" ? "chip-halt" : row.session === "AH" ? "chip-ah" : ""}`}>
            {row.session === "HALT" ? "Halt" : row.session === "AH" ? "After hours" : "RTH"}
          </span>
          <span style={{ color: "var(--ink-muted)", fontSize: 12 }}>vol {row.vol}</span>
        </div>
      </div>

      <div className="nav-ticket-grid" style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 44, marginTop: 32 }}>
        <div>
          <div style={{ display: "flex", gap: 18, marginBottom: 10 }} data-testid="tf-toggle">
            {["1H", "1D", "1W"].map((tt) => (
              <button key={tt} onClick={() => setTf(tt)} className="btn-ghost" style={{ opacity: tf === tt ? 1 : 0.4 }}>{tt}</button>
            ))}
          </div>
          <TimeChart ticker={row.ticker} delta={d} halted={halted} history={history} />
          <div style={{ display: "flex", gap: 20, color: "var(--ink-muted)", fontSize: 12, marginTop: 8 }}>
            <span><span style={{ display: "inline-block", width: 18, borderTop: "1.5px solid var(--ink)", verticalAlign: "middle", marginRight: 6 }} /> Pool</span>
            <span><span style={{ display: "inline-block", width: 18, borderTop: "1.5px dashed var(--line)", verticalAlign: "middle", marginRight: 6 }} /> NAV</span>
            {row.poolAddr && (
              <a href={`https://robinhoodchain.blockscout.com/address/${row.poolAddr}`} target="_blank" rel="noreferrer" style={{ color: "var(--ink-muted)", textDecoration: "underline" }}>
                pool {row.poolAddr.slice(0,6)}…{row.poolAddr.slice(-4)} · fee {row.fee/10000}%
              </a>
            )}
          </div>

          <div style={{ marginTop: 36, borderTop: "1px solid var(--line)", paddingTop: 18 }}>
            <div className="grotesque" style={{ fontSize: 10.5, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-muted)", marginBottom: 10 }}>
              Pairs quoted in {row.ticker}
            </div>
            {relatedPairs.length === 0 ? (
              <div style={{ color: "var(--ink-muted)", fontSize: 13 }}>None yet.</div>
            ) : (
              <table className="book tabular">
                <thead><tr><th>Pair</th><th>Venue</th><th>Last</th><th>Liq</th><th>24h</th></tr></thead>
                <tbody>
                  {relatedPairs.map((p) => (
                    <tr key={p.pair}>
                      <td>{p.pair}</td>
                      <td style={{ color: "var(--ink-muted)" }}>Uniswap {p.venue}</td>
                      <td>{fmt(p.last)}</td>
                      <td>{p.liq}</td>
                      <td>{p.vol24}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div
          className={`trade-sheet-backdrop ${sheetOpen ? "open" : ""}`}
          onClick={() => setSheetOpen(false)}
          data-testid="trade-sheet-backdrop"
        />
        <aside
          className={`trade-sheet ${sheetOpen ? "open" : ""}`}
          style={{ border: "1px solid var(--line)", padding: 22, background: "var(--paper-raised)" }}
          data-testid="trade-ticket"
        >
          <button
            className="trade-sheet-handle"
            onClick={() => setSheetOpen(false)}
            data-testid="trade-sheet-close"
            aria-label="Close ticket"
          >
            <span />
          </button>
          <div style={{ display: "flex", gap: 0, marginBottom: 18 }}>
            <button
              onClick={() => setTabHandler("trade")}
              data-testid="tab-trade"
              style={{
                flex: 1, padding: "10px 0", fontSize: 11.5, cursor: "pointer",
                background: tab === "trade" ? "var(--ink)" : "transparent",
                color: tab === "trade" ? "var(--paper)" : "var(--ink)",
                border: "1px solid var(--ink)", borderRight: "none",
                letterSpacing: "0.14em", textTransform: "uppercase",
              }}
            >Trade</button>
            <button
              onClick={() => setTabHandler("range")}
              data-testid="tab-range"
              style={{
                flex: 1, padding: "10px 0", fontSize: 11.5, cursor: "pointer",
                background: tab === "range" ? "var(--ink)" : "transparent",
                color: tab === "range" ? "var(--paper)" : "var(--ink)",
                border: "1px solid var(--ink)",
                letterSpacing: "0.14em", textTransform: "uppercase",
              }}
            >Range</button>
          </div>

          {tab === "trade" ? (
            <>
              <div style={{ display: "flex", gap: 0, marginBottom: 16 }}>
                <button
                  onClick={() => setSideHandler("buy")}
                  data-testid="side-buy"
                  disabled={halted}
                  style={{
                    flex: 1, padding: "12px 0", fontSize: 12,
                    background: !halted && side === "buy" ? "var(--ink)" : "transparent",
                    color: !halted && side === "buy" ? "var(--paper)" : "var(--ink)",
                    border: "1px solid var(--ink)", borderRight: "none",
                    opacity: halted ? 0.35 : 1, cursor: halted ? "not-allowed" : "pointer",
                    letterSpacing: "0.06em",
                  }}
                >BUY</button>
                <button
                  onClick={() => setSideHandler("sell")}
                  data-testid="side-sell"
                  disabled={halted}
                  style={{
                    flex: 1, padding: "12px 0", fontSize: 12,
                    background: !halted && side === "sell" ? "var(--ink)" : "transparent",
                    color: !halted && side === "sell" ? "var(--paper)" : "var(--ink)",
                    border: "1px solid var(--ink)",
                    opacity: halted ? 0.35 : 1, cursor: halted ? "not-allowed" : "pointer",
                    letterSpacing: "0.06em",
                  }}
                >SELL</button>
              </div>

              <label style={{ fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-muted)" }}>Pay</label>
              <select value={pay} onChange={(e) => setPay(e.target.value)} data-testid="pay-select" disabled={halted || swapBusy || side === "sell"}
                      style={{ width: "100%", padding: "10px 8px", marginTop: 6, marginBottom: 14, background: "transparent", border: "1px solid var(--line)", color: "var(--ink)", fontSize: 14 }}>
                <option>USDG</option>
                {side === "buy" && <option>ETH</option>}
              </select>

              <label style={{ fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-muted)" }}>Receive</label>
              <div style={{ padding: "10px 8px", border: "1px solid var(--line)", marginTop: 6, marginBottom: 14, fontSize: 14 }}>
                {row.ticker}
              </div>

              <label style={{ fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-muted)" }}>
                Size {side === "buy" ? `(${pay})` : `(${row.ticker})`}
              </label>
              <input
                value={size}
                onChange={(e) => setSize(e.target.value.replace(/[^0-9.]/g, ""))}
                placeholder="0.00"
                data-testid="size-input"
                disabled={halted || swapBusy}
                style={{ width: "100%", padding: "10px 8px", marginTop: 6, marginBottom: 12, background: "transparent", border: "1px solid var(--line)", color: "var(--ink)", fontSize: 15 }}
              />

              <label style={{ fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-muted)" }}>Slippage</label>
              <div style={{ display: "flex", gap: 8, marginTop: 6, marginBottom: 14 }}>
                {["0.1", "0.5", "1.0", "3.0"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setSlippage(s)}
                    data-testid={`slip-${s}`}
                    style={{
                      flex: 1, padding: "8px 0", fontSize: 11.5, cursor: "pointer",
                      background: slippage === s ? "var(--ink)" : "transparent",
                      color: slippage === s ? "var(--paper)" : "var(--ink)",
                      border: "1px solid var(--line)",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {s}%
                  </button>
                ))}
              </div>

              <div className="tabular" style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: "var(--ink-muted)", marginBottom: 4 }}>
                <span>Route</span><span>Uniswap v3{row.fee ? ` · ${row.fee/10000}%` : ""}</span>
              </div>
              <div className="tabular" style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: "var(--ink-muted)", marginBottom: 4 }}>
                <span>Est receive</span>
                <span>
                  {size && row.pool
                    ? (side === "buy"
                        ? `${(Number(size) / row.pool).toFixed(6)} ${row.ticker}`
                        : `${(Number(size) * row.pool).toFixed(2)} USDG`)
                    : "—"}
                </span>
              </div>
              <div className="tabular" style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: "var(--ink-muted)", marginBottom: 18 }}>
                <span>Depth vs pool</span><span>{priceImpact.toFixed(2)}%</span>
              </div>

              {!connected ? (
                <button
                  className="btn-primary"
                  onClick={() => setOpen(true)}
                  data-testid="connect-to-trade"
                  style={{ width: "100%", padding: "14px", fontSize: 13, letterSpacing: "0.06em" }}
                >
                  CONNECT TO TRADE
                </button>
              ) : (
                <button
                  className="btn-primary"
                  disabled={halted || swapBusy || !canTrade}
                  data-testid="submit-trade"
                  onClick={() => submitSwap({
                    side, ticker: row.ticker,
                    token: row.token, tokenDec: 18,
                    fee: row.fee, poolPrice: row.pool,
                    sizeStr: size, slippagePct: Number(slippage) || 0.5,
                    payAsset: pay,
                  })}
                  style={{ width: "100%", padding: "14px", fontSize: 13, letterSpacing: "0.06em" }}
                >
                  {halted
                    ? "HALT"
                    : swapBusy
                      ? (swapStage === "approve" ? "APPROVING…" : swapStage === "swap" ? "SWAPPING…" : "CONFIRMING…")
                      : wrongChain
                        ? "SWITCH TO 4663"
                        : `${side === "buy" ? "BUY" : "SELL"} ${row.ticker}`}
                </button>
              )}

              <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--line)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, letterSpacing: "0.08em", color: "var(--ink-muted)", textTransform: "uppercase" }}>
                  <span>Official list</span><span>Yes</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, letterSpacing: "0.08em", color: "var(--ink-muted)", marginTop: 6, textTransform: "uppercase" }}>
                  <span>Quote</span><span>USDG</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, letterSpacing: "0.08em", color: "var(--ink-muted)", marginTop: 6, textTransform: "uppercase" }}>
                  <span>Chain</span><span>Robinhood 4663</span>
                </div>
                {row.token && (
                  <div style={{ marginTop: 12, fontSize: 10.5, letterSpacing: "0.06em", color: "var(--ink-muted)" }}>
                    <a href={`https://robinhoodchain.blockscout.com/token/${row.token}`} target="_blank" rel="noreferrer" style={{ color: "var(--ink-muted)", textDecoration: "underline" }}>
                      Token {row.token.slice(0,6)}…{row.token.slice(-4)}
                    </a>
                  </div>
                )}
              </div>
            </>
          ) : (
            <RangePanel ticker={row.ticker} pool={row.pool} />
          )}
        </aside>
      </div>

      {/* Mobile: floating bottom action bar (opens the sheet). Hidden on desktop via CSS. */}
      <div className="trade-fab" data-testid="trade-fab">
        <div className="tabular" style={{ display: "flex", flexDirection: "column", justifyContent: "center", lineHeight: 1.15 }}>
          <span style={{ fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-muted)" }}>{row.ticker} · Pool</span>
          <span style={{ fontSize: 16 }}>{fmt(row.pool)}</span>
        </div>
        <button
          className="btn-primary"
          onClick={() => setSheetOpen(true)}
          data-testid="open-trade-sheet"
          disabled={halted}
          style={{ flex: 1, padding: "12px", fontSize: 13, letterSpacing: "0.06em", opacity: halted ? 0.35 : 1 }}
        >
          {halted ? "HALT" : `${side === "buy" ? "BUY" : "SELL"} ${row.ticker}`}
        </button>
      </div>
    </div>
  );
}
